import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Send, BookOpen, RefreshCw, AlertCircle, Trash2, History, Plus, ChevronDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BookRecommendationCard } from './BookRecommendationCard';
import { useRecommendationFeedback } from '@/hooks/useRecommendationFeedback';
import type { BookRecommendation } from '@/hooks/useBookRecommendations';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { SafeHtml } from './SafeHtml';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';

// Convert markdown to HTML for assistant messages
function markdownToHtml(text: string): string {
  let result = text;
  
  // Handle bullet points: * item or - item at start of line
  result = result.replace(/^[\*\-]\s+(.+)$/gm, '<li>$1</li>');
  // Wrap consecutive li's in ul
  result = result.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');
  
  // Bold: **text** or __text__ (do this before italic)
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Italic: *text* or _text_ (single asterisk/underscore)
  result = result.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');
  result = result.replace(/(?<!_)_([^_\n]+)_(?!_)/g, '<em>$1</em>');
  
  // Line breaks (but not inside lists)
  result = result.replace(/\n(?!<)/g, '<br />');
  
  return result;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  recommendations?: BookRecommendation[];
  timestamp: Date;
  isError?: boolean;
}

interface ChatSession {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export function SmartBookChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fromTBROnly, setFromTBROnly] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatSession[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { submitFeedback } = useRecommendationFeedback();

  // Load chat history list
  const loadChatHistory = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('recommendation_chats')
        .select('id, title, created_at, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      setChatHistory(data || []);
    } catch (err) {
      console.error('Failed to load chat history:', err);
    }
  }, [user]);

  // Load messages for a specific chat
  const loadChatMessages = useCallback(async (chatId: string) => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('recommendation_chat_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      const loadedMessages: ChatMessage[] = (data || []).map(msg => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        recommendations: msg.recommendations as unknown as BookRecommendation[] | undefined,
        timestamp: new Date(msg.created_at),
        isError: msg.is_error || false,
      }));
      
      setMessages(loadedMessages);
      setCurrentChatId(chatId);
    } catch (err) {
      console.error('Failed to load chat messages:', err);
      toast.error('Failed to load chat');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Create a new chat session
  const createNewChat = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    
    try {
      const { data, error } = await supabase
        .from('recommendation_chats')
        .insert({ user_id: user.id })
        .select('id')
        .single();
      
      if (error) throw error;
      return data.id;
    } catch (err) {
      console.error('Failed to create chat:', err);
      return null;
    }
  }, [user]);

  // Save a message to the database
  const saveMessage = useCallback(async (chatId: string, message: ChatMessage) => {
    try {
      await supabase
        .from('recommendation_chat_messages')
        .insert([{
          chat_id: chatId,
          role: message.role,
          content: message.content,
          recommendations: (message.recommendations || null) as unknown as undefined,
          is_error: message.isError || false,
        }]);
      
      // Update chat title if it's the first user message
      if (message.role === 'user') {
        const title = message.content.slice(0, 50) + (message.content.length > 50 ? '...' : '');
        await supabase
          .from('recommendation_chats')
          .update({ title, updated_at: new Date().toISOString() })
          .eq('id', chatId);
      }
    } catch (err) {
      console.error('Failed to save message:', err);
    }
  }, []);

  // Load history on mount
  useEffect(() => {
    if (user) {
      loadChatHistory();
    }
  }, [user, loadChatHistory]);

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = useCallback(async (messageOverride?: string) => {
    const messageToSend = messageOverride || input.trim();
    if (!messageToSend || loading) return;

    let chatId = currentChatId;
    
    // Create new chat if needed
    if (!chatId) {
      chatId = await createNewChat();
      if (!chatId) {
        toast.error('Failed to start chat');
        return;
      }
      setCurrentChatId(chatId);
    }

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageToSend,
      timestamp: new Date(),
    };

    // Only add user message if not a retry
    if (!messageOverride) {
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      // Save to database
      saveMessage(chatId, userMessage);
    }
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to get recommendations');
        setLoading(false);
        return;
      }

      // Build conversation context for the AI (excluding error messages)
      const conversationContext = messages
        .filter(m => !m.isError)
        .map(m => ({
          role: m.role,
          content: m.role === 'user' 
            ? m.content 
            : m.recommendations 
              ? `I recommended: ${m.recommendations.map(r => `"${r.title}" by ${r.author}`).join(', ')}. ${m.content}`
              : m.content
        }));

      const { data, error } = await supabase.functions.invoke('recommend-books-chat', {
        body: { 
          message: messageToSend,
          conversationHistory: conversationContext,
          fromTBROnly,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message || 'Here are some recommendations for you:',
        recommendations: data.recommendations,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      saveMessage(chatId, assistantMessage);
      setRetryCount(0);
      loadChatHistory(); // Refresh history list
    } catch (error) {
      console.error('❌ Chat error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isRateLimit = errorMessage.includes('429') || errorMessage.includes('rate limit');
      const isPayment = errorMessage.includes('402') || errorMessage.includes('payment');
      
      let userFriendlyMessage = "Sorry, I couldn't process that request.";
      if (isRateLimit) {
        userFriendlyMessage = "I'm getting too many requests right now. Please wait a moment and try again.";
      } else if (isPayment) {
        userFriendlyMessage = "The AI service is temporarily unavailable. Please try again later.";
      }
      
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: userFriendlyMessage,
        timestamp: new Date(),
        isError: true,
      };
      
      setMessages(prev => [...prev, errorMsg]);
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, fromTBROnly, currentChatId, createNewChat, saveMessage, loadChatHistory]);

  const handleRetry = useCallback(() => {
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
    if (lastUserMessage) {
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.isError) {
          return prev.slice(0, -1);
        }
        return prev;
      });
      handleSend(lastUserMessage.content);
    }
  }, [messages, handleSend]);

  const handleDismissRecommendation = async (messageId: string, rec: BookRecommendation) => {
    await submitFeedback({
      bookId: rec.bookId,
      bookTitle: rec.title,
      bookAuthor: rec.author,
      isbn13: rec.isbn13,
      isbn10: rec.isbn10,
      feedbackType: 'dismissed',
      recommendationReason: rec.reason,
      sessionId: rec.sessionId,
    });

    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId && msg.recommendations) {
        return {
          ...msg,
          recommendations: msg.recommendations.filter(r => r.title !== rec.title),
        };
      }
      return msg;
    }));

    setInput(`Not "${rec.title}" - can you suggest something different?`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentChatId(null);
    setRetryCount(0);
  };

  const deleteChat = async (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await supabase
        .from('recommendation_chats')
        .delete()
        .eq('id', chatId);
      
      if (currentChatId === chatId) {
        startNewChat();
      }
      loadChatHistory();
      toast.success('Chat deleted');
    } catch (err) {
      console.error('Failed to delete chat:', err);
      toast.error('Failed to delete chat');
    }
  };

  const suggestedPrompts = [
    "Something cozy for a rainy day",
    "A gripping thriller I can't put down",
    "Do you think I'd like [book name]?",
    "Books similar to what I've loved recently",
    "Help me pick from my TBR pile",
  ];

  const lastMessageIsError = messages.length > 0 && messages[messages.length - 1]?.isError;

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Smart Book Chat</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {/* Chat History Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" title="Chat history">
                  <History className="w-4 h-4 mr-1" />
                  History
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem onClick={startNewChat} className="gap-2">
                  <Plus className="w-4 h-4" />
                  New Chat
                </DropdownMenuItem>
                {chatHistory.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="max-h-64 overflow-y-auto">
                      {chatHistory.map((chat) => (
                        <DropdownMenuItem
                          key={chat.id}
                          onClick={() => loadChatMessages(chat.id)}
                          className={cn(
                            "flex items-center justify-between gap-2 group",
                            currentChatId === chat.id && "bg-accent"
                          )}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">
                              {chat.title || 'Untitled chat'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(chat.updated_at), { addSuffix: true })}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => deleteChat(chat.id, e)}
                          >
                            <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                          </Button>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </>
                )}
                {chatHistory.length === 0 && (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    No previous chats
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <CardDescription>
          Have a conversation to find your next perfect read
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* TBR Toggle */}
        <div className="flex items-center space-x-2 pb-2">
          <Switch
            id="tbr-mode"
            checked={fromTBROnly}
            onCheckedChange={setFromTBROnly}
          />
          <Label htmlFor="tbr-mode" className="text-sm cursor-pointer">
            <BookOpen className="w-4 h-4 inline mr-1" />
            Recommend from my TBR only
          </Label>
        </div>

        {/* Chat Messages */}
        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length > 0 ? (
          <div ref={scrollContainerRef} className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex flex-col gap-2",
                  message.role === 'user' ? "items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "rounded-lg px-4 py-2 max-w-[85%]",
                    message.role === 'user'
                      ? "bg-primary text-primary-foreground"
                      : message.isError
                        ? "bg-destructive/10 border border-destructive/20"
                        : "bg-muted"
                )}
                >
                  {message.isError && (
                    <AlertCircle className="w-4 h-4 text-destructive inline mr-2" />
                  )}
                  {message.role === 'assistant' ? (
                    <SafeHtml 
                      html={markdownToHtml(message.content)} 
                      className="text-sm prose prose-sm max-w-none [&>br]:leading-relaxed" 
                    />
                  ) : (
                    <p className="text-sm whitespace-pre-wrap inline">{message.content}</p>
                  )}
                </div>
                
                {message.recommendations && message.recommendations.length > 0 && (
                  <div className="w-full space-y-2 mt-2">
                    {message.recommendations.map((rec, idx) => (
                      <div key={idx} className="p-2 rounded-lg bg-background/50 hover:bg-background transition-colors">
                        <BookRecommendationCard
                          {...rec}
                          compact
                          onDismiss={() => handleDismissRecommendation(message.id, rec)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Loading State */}
            {loading && (
              <div className="flex flex-col gap-2 items-start">
                <div className="rounded-lg px-4 py-2 bg-muted animate-pulse">
                  <p className="text-sm text-muted-foreground">Thinking...</p>
                </div>
                <div className="w-full space-y-2 mt-2">
                  {[...Array(3)].map((_, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-background/50">
                      <div className="w-16 aspect-[2/3] bg-muted rounded animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
                        <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Retry Button for Errors */}
            {lastMessageIsError && !loading && (
              <div className="flex justify-center">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry}
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try again
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Suggested Prompts */
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => setInput(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="flex gap-2 pt-2 border-t">
          <Textarea
            placeholder={fromTBROnly 
              ? "What should I read from my TBR?" 
              : "What kind of book are you in the mood for?"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[60px] max-h-[120px] resize-none"
            disabled={loading}
          />
          <Button
            onClick={() => handleSend()}
            disabled={loading || !input.trim()}
            size="icon"
            className="h-auto aspect-square"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
